import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DataGrid } from "../../components/dataGrid/dataGrid.tsx";
import { ParamType } from "../../types/grid.ts";
import {
    getCustomerEvent,
    updateCustomerEventStatus,
    CustomerEvent,
    EventProduct,
} from "../../api/specialized.ts";
import { EditCartModal } from "../../components/transactionOps/editCartModal.tsx";
import { VideoModal } from "../../components/transactionOps/videoModal.tsx";
import { PagePopup } from "../../components/dataGrid/pagePopup.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { formatInstant, parseUtcTimestamp } from "../../util/serverTime.ts";
import { collapseSngCartSnapshot, isSngSession } from "./sngCustomerEvents.ts";
import styles from "../../styles/formPage.module.scss";

/* ── Color map for event rows ──────────────────────────── */
const EVENT_COLORS: Record<string, string> = {
    ENTERED: "#4a90d9",
    EXITED: "#d97a4a",
    DETECTED: "#888",
    GATEOPEN: "#888",
    GATECLOSE: "#888",
    LOST: "#cc3333",
    FOUND: "#33aa33",
    ADDED: "#2e8b57",
    OPENED: "#2e8b57",
    REMOVED: "#cc4444",
    CLOSED: "#cc4444",
    SWAP: "#9b59b6",
    INITIAL: "#607d8b",
    CART: "#795548",
    MISPLACEMENT: "#e67e22",
    SCANNED: "#3498db",
    LINK: "#27ae60",
    OTHER: "#888",
    WEIGHT: "#8e44ad",
};

/* SNG cart line at payment, set apart from the green SCAN rows */
const CART_AT_PAYMENT_COLOR = "#0f766e";

function getEventColor(ev: EventProduct): string {
    if (ev.CartAtPayment) return CART_AT_PAYMENT_COLOR;
    if (ev.Event.includes("PAYMENT")) return "#f39c12";
    return EVENT_COLORS[ev.Event] ?? "#666";
}

/* ── Product-type events that show the product detail panel ── */
const PRODUCT_EVENTS = new Set([
    "WEIGHT", "ADDED", "SCANNED", "REMOVED", "OTHER",
    "LINK", "MISPLACEMENT", "CART", "INITIAL", "SWAP",
]);

/* ── Format event display name ────────────────────────── */
function eventDisplayName(ev: EventProduct): string {
    if (ev.Event === "ENTERED") return "ENTERED";
    if (ev.Event === "EXITED") return "EXITED";
    if (ev.Event === "DETECTED") return "DETECTED";
    if (ev.Event === "GATEOPEN") return "GATE OPENED";
    if (ev.Event === "GATECLOSE") return "GATE CLOSED";
    if (ev.Event === "GATEINTRUDER") return "GATE INTRUDER";
    if (ev.Event === "LOST") return "LOST";
    if (ev.Event === "FOUND") return "FOUND";
    if (ev.Event === "OTHER") return "OTHER";
    if (ev.Event === "OPENED") return "OPENED";
    if (ev.Event === "CLOSED") return "CLOSED";
    if (ev.Event === "SWAP") return "SWAP";
    if (ev.Event === "INITIAL") return "INITIAL";
    if (ev.Event === "CART") return "CART";
    if (ev.Event === "MISPLACEMENT") return "MISPLACEMENT";
    if (ev.Event === "SCANNED") return `SCAN: ${ev.ProductID}. ${ev.Name}`;
    if (ev.CartAtPayment) return `PAID: ${ev.ProductID}. ${ev.Name}`;
    if (ev.Event === "REMOVED" || ev.Event === "ADDED") return `EDIT: ${ev.ProductID}. ${ev.Name}`;
    if (ev.Event === "LINK") return `LINK ${ev.Name}`;
    if (ev.Event.includes("PAYMENT")) return ev.Event.split("|")[1] ?? ev.Event;
    if (ev.Name) return `${ev.ProductID}. ${ev.Name}`;
    return ev.Event;
}

/* ── Format date/time ─────────────────────────────────── */

/** S_Customer_Event.Started/Ended hold UTC but serialize without a zone, so a
 *  bare `new Date(s)` read them as browser-local — two hours early in Sweden,
 *  and the "UTC:" line underneath then reported a UTC that never existed. */
function fmt(dateStr: string | null | undefined): string {
    if (!dateStr) return "—";
    return formatInstant(parseUtcTimestamp(dateStr)) || String(dateStr);
}

function fmtUtc(dateStr: string | null | undefined): string {
    const d = parseUtcTimestamp(dateStr);
    if (!d) return "";
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const mm = String(d.getUTCMinutes()).padStart(2, "0");
    const ss = String(d.getUTCSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
}

/* ── Main page ────────────────────────────────────────── */
export function CustomerEventsPage({ popupSid, popupTid, onClose }: { popupSid?: string; popupTid?: number; onClose?: (didChange?: boolean) => void } = {}) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const instantID = popupSid ?? searchParams.get("sid") ?? "";
    const transactionID = popupTid ?? (parseInt(searchParams.get("tid") ?? "0", 10) || 0);

    const [data, setData] = useState<CustomerEvent | null>(null);
    const [events, setEvents] = useState<EventProduct[]>([]);
    const [selected, setSelected] = useState<EventProduct | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [resolveNote, setResolveNote] = useState("");
    const [resolveOpen, setResolveOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showEditCart, setShowEditCart] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    const [kioskPopupPath, setKioskPopupPath] = useState<string | null>(null);
    const [camIdent, setCamIdent] = useState<string | undefined>(undefined);

    const load = useCallback(async () => {
        if (!instantID) { setError("No InstantID (sid) in URL"); setLoading(false); return; }
        setLoading(true);
        try {
            const res = await getCustomerEvent(instantID);
            if (res.bError || !res.oData) {
                setError(res.sMessage ?? "Failed to load event data");
                return;
            }
            const ev = res.oData;
            setData(ev);

            // Extract identity camera InstantID from CAMERA event
            const allEvents = ev.EventList ?? [];
            const camEvt = allEvents.find(e => e.Event.toLowerCase().includes("camera"));
            setCamIdent(camEvt?.Extra || undefined);

            // Compute elapsed seconds from start event. An SNG session opens
            // at INITIAL and pays last, so it counts from INITIAL instead.
            const list = collapseSngCartSnapshot(allEvents.filter(e => e.Event !== "CAMERA"));
            const sng = isSngSession(list);
            let start: Date | null = null;
            for (const e of sng ? [] : list) {
                const lower = e.Event.toLowerCase();
                if ((lower.includes("payment") || lower.includes("entered")) && !lower.includes("coop")) {
                    start = new Date(e.When);
                    break;
                }
            }
            if (!start) {
                for (const e of list) {
                    if (e.Event.toLowerCase().includes("initial")) { start = new Date(e.When); break; }
                }
            }
            if (!start) {
                for (const e of list) {
                    if (e.Event.toLowerCase().includes("detected")) { start = new Date(e.When); break; }
                }
            }
            if (start) {
                const startMs = start.getTime();
                for (const e of list) {
                    e.Seconds = Math.floor((new Date(e.When).getTime() - startMs) / 1000);
                }
            }
            setEvents(list);
            setError(null);
        } catch (e) {
            setError(String(e));
        } finally {
            setLoading(false);
        }
    }, [instantID]);

    // Dedupe by instantID — StrictMode double-mount would fire WebGetCustomerEvent twice.
    const lastLoadKeyRef = useRef<string>("");
    useEffect(() => {
        if (lastLoadKeyRef.current === instantID) return;
        lastLoadKeyRef.current = instantID;
        load();
    }, [load, instantID]);

    // Tracks whether the user resolved the event or saved a cart edit. Closing
    // without changes signals the parent grid to skip the refresh.
    const dirtyRef = useRef(false);

    const handleResolve = async () => {
        setSaving(true);
        try {
            const res = await updateCustomerEventStatus(instantID, true, resolveNote);
            if (res.bError) {
                setMessage(`Error: ${res.sMessage ?? "Failed"}`);
            } else {
                setMessage("Event resolved: " + instantID);
                setResolveOpen(false);
                setResolveNote("");
                dirtyRef.current = true;
                load();
            }
        } finally {
            setSaving(false);
        }
    };

    const closeModal = onClose
        ? () => onClose(dirtyRef.current)
        : () => navigate(-1);

    // The event payload carries the transaction id that belongs to this InstantID.
    // Prefer it over the caller's `tid`, so a caller that only knows the InstantID,
    // or passes an id from another system, still gets the transaction grids and
    // payment logs of the right transaction.
    const gridTransactionID = (data?.TransactionID ?? 0) > 0 ? data!.TransactionID : transactionID;

    // Auxiliary actions bubble into ModalFooter (footerLeft) per AGENTS.md
    // § Create/Edit Modal Guidelines.
    const footerLeft = data ? (
        <>
            <button className={styles.secondaryBtn} onClick={() => setShowVideo(true)}>🎥 Video</button>
            <button className={styles.secondaryBtn} onClick={() => setShowEditCart(true)}>🛒 Edit Cart</button>
            <button className={styles.dangerBtn} onClick={() => setResolveOpen(v => !v)}>✅ Resolve</button>
        </>
    ) : null;

    return (
        <GridEditModal
            title="Customer Event"
            wide
            maxWidth="1400px"
            readOnly
            cancelLabel="Close"
            loading={loading}
            footerLeft={footerLeft}
            statusMessage={
                error ? { type: "error", message: error }
                : message ? { type: message.startsWith("Error") ? "error" : "success", message }
                : null
            }
            onClose={closeModal}
            onSave={() => { /* read-only */ }}
        >
            {!data ? (
                <div>No data</div>
            ) : (
                <>
                    {/* Resolve inline panel */}
                    {resolveOpen && (
                        <div style={{
                            border: "1px solid var(--color-border)", borderRadius: "var(--radius-2)",
                            padding: "var(--space-3)", marginBottom: "var(--space-3)", background: "var(--color-bg-muted)",
                            maxWidth: 400, display: "flex", flexDirection: "column", gap: "var(--space-2)",
                        }}>
                            <label style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-medium)" }}>Resolved Note:</label>
                            <input className={styles.fieldInput} value={resolveNote} onChange={e => setResolveNote(e.target.value)} />
                            <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                <button className={styles.dangerBtn} onClick={handleResolve} disabled={saving}>{saving ? "..." : "Resolve"}</button>
                                <button className={styles.secondaryBtn} onClick={() => setResolveOpen(false)}>Cancel</button>
                            </div>
                        </div>
                    )}

                    <OverviewHeader data={data} onKioskClick={(id) => setKioskPopupPath(`/page/editkiosk?edit=${id}`)} />

                    {data.InspectionReason && (
                        <div style={{ marginBottom: "var(--space-2)", fontSize: "var(--text-sm)" }}>
                            <strong>Inspection Reason:</strong> {data.InspectionReason}
                        </div>
                    )}
                    <div style={{ marginBottom: "var(--space-3)", fontSize: "var(--text-sm)" }}>
                        <strong>Entered Count:</strong> {data.EnteredCount}
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-4)", marginBottom: "var(--space-4)" }}>
                        <EventList events={events} selected={selected} onSelect={setSelected} />
                        <EventDetailPanel event={selected} />
                    </div>

                    {gridTransactionID > 0 && <TransactionGrids transactionID={gridTransactionID} />}

                    {showEditCart && (
                        <EditCartModal
                            open={showEditCart}
                            instantID={instantID}
                            onClose={() => setShowEditCart(false)}
                            onSaved={() => { dirtyRef.current = true; load(); }}
                        />
                    )}

                    {showVideo && (
                        <VideoModal
                            open={showVideo}
                            kioskID={data.KioskID}
                            instantID={instantID}
                            camIdent={camIdent}
                            showReturns
                            onClose={() => setShowVideo(false)}
                        />
                    )}

                    {kioskPopupPath && (
                        <PagePopup path={kioskPopupPath} onClose={() => setKioskPopupPath(null)} />
                    )}
                </>
            )}
        </GridEditModal>
    );
}

/* ── Overview header ──────────────────────────────────── */
function OverviewHeader({ data, onKioskClick }: { data: CustomerEvent; onKioskClick: (kioskID: number) => void }) {
    return (
        <div style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 140px), 1fr))", gap: "var(--space-2)",
            marginBottom: "var(--space-3)", fontSize: "var(--text-sm)",
        }}>
            <InfoBox>
                <div><strong>Kiosk ID:</strong></div>
                <a href="#" onClick={e => { e.preventDefault(); onKioskClick(data.KioskID); }}
                    style={{ color: "var(--color-info)", textDecoration: "underline", cursor: "pointer" }}>
                    <div>{data.KioskID}</div>
                </a>
            </InfoBox>
            <InfoBox>
                <div><strong>Instant ID:</strong></div>
                <div>{data.InstantID}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Customer Reference:</strong></div>
                <div>{data.CustomerReference ?? "—"}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Email:</strong> {data.Email ?? "—"}</div>
                <div><strong>Tel:</strong> {data.Phone ?? "—"}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Edited Cart Manually:</strong></div>
                <div>{data.EditedCartManually ? "Yes" : "No"}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Marked for Inspection:</strong></div>
                <div>{data.MarkedInspection ? "Yes" : "No"}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Started:</strong> {fmt(data.Started)}</div>
                <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>UTC: {fmtUtc(data.Started)}</div>
            </InfoBox>
            <InfoBox>
                <div><strong>Ended:</strong> {fmt(data.Ended)}</div>
                <div style={{ color: "var(--color-text-muted)", fontSize: "var(--text-xs)" }}>UTC: {fmtUtc(data.Ended)}</div>
            </InfoBox>
        </div>
    );
}

function InfoBox({ children }: { children: React.ReactNode }) {
    return (
        <div style={{
            padding: "var(--space-2)", background: "var(--color-bg-muted)",
            borderRadius: "var(--radius-1)", border: "1px solid var(--color-border)",
        }}>
            {children}
        </div>
    );
}

/* ── Event list (left panel) ──────────────────────────── */
function EventList({ events, selected, onSelect }: {
    events: EventProduct[];
    selected: EventProduct | null;
    onSelect: (e: EventProduct) => void;
}) {
    return (
        <div style={{
            width: 320, minWidth: 0, maxWidth: "100%", maxHeight: 600, overflowY: "auto",
            border: "1px solid var(--color-border)", borderRadius: "var(--radius-1)",
        }}>
            {events.map((ev, i) => {
                const isSelected = selected === ev;
                const name = eventDisplayName(ev);
                const isProductEvent = ev.Event === "SCANNED" || ev.Event === "REMOVED" || ev.Event === "ADDED" || ev.Event === "LINK" || (ev.Name && !["ENTERED", "EXITED", "DETECTED", "LOST", "FOUND", "GATEOPEN", "GATECLOSE", "OTHER", "OPENED", "CLOSED", "INITIAL", "CART", "MISPLACEMENT", "SWAP"].includes(ev.Event) && !ev.Event.includes("PAYMENT"));
                const showQty = isProductEvent;
                const validBg = ev.CartAtPayment ? "rgba(15,118,110,0.06)"
                    : isProductEvent
                    ? ev.IsValid ? "rgba(34,197,94,0.08)" : "rgba(239,68,68,0.08)"
                    : undefined;
                return (
                    <div
                        key={i}
                        onClick={() => onSelect(ev)}
                        style={{
                            padding: "var(--space-2) var(--space-3)",
                            cursor: "pointer",
                            borderBottom: "1px solid var(--color-border)",
                            background: isSelected ? "var(--color-bg-muted)" : validBg,
                            borderLeft: `4px solid ${getEventColor(ev)}`,
                        }}
                    >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <strong style={{ fontSize: "var(--text-sm)" }}>
                                {name.length > 28 ? name.substring(0, 28) + ".." : name}
                            </strong>
                            <span style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>
                                {ev.Seconds != null ? `${ev.Seconds}s` : ""}
                            </span>
                        </div>
                        {showQty && ev.Quantity != null && (
                            <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)" }}>QTY: {ev.Quantity}</div>
                        )}
                    </div>
                );
            })}
            {events.length === 0 && (
                <div style={{ padding: "var(--space-4)", textAlign: "center", color: "var(--color-text-muted)" }}>No events</div>
            )}
        </div>
    );
}

/* ── Event detail panel (right side) ──────────────────── */
function EventDetailPanel({ event }: { event: EventProduct | null }) {
    if (!event) {
        return (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-text-muted)" }}>
                Select an event to view details
            </div>
        );
    }

    const isProduct = PRODUCT_EVENTS.has(event.Event);

    if (isProduct) return <ProductDetail ev={event} />;
    return <TrackingDetail ev={event} />;
}

function ProductDetail({ ev }: { ev: EventProduct }) {
    const when = new Date(ev.When);
    const accessLabel = ev.AccessLevel === 0 ? "Customer" : ev.AccessLevel === 1 ? "Merchandiser" : "Technician";

    return (
        <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: "var(--space-3)" }}>
            <fieldset style={{ flex: "1 1 240px", minWidth: 0, border: "1px solid var(--color-border)", borderRadius: "var(--radius-1)", padding: "var(--space-3)" }}>
                <legend><strong>Product Data</strong></legend>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
                    <div>Product: {ev.Name}</div>
                    <div>Product ID: {ev.ProductID}</div>
                    <div>Slot: {ev.SlotName} (ID: {ev.SlotID})</div>
                    <div>Quantity: {ev.Quantity}</div>
                    <div>Is Putback: {ev.Weight > 0 ? "Yes" : "No"}</div>
                    <div>Weight: {ev.Weight}</div>
                    <div>Product Weight: {ev.ProductWeight}</div>
                    <div>Is Valid: {ev.IsValid ? "Yes" : "No"}</div>
                    <div>Marked Uncertain: {String(ev.MarkedAsUncertain)}</div>
                    <div>Had similar weight in hand: {ev.HadSimilarWeightInHand ? "Yes" : "No"}</div>
                    <div>Had product in hand: {ev.HadProductInHand ? "Yes" : "No"}</div>
                    <div>Access Level: {accessLabel}</div>
                    <div>Did Infect: {ev.Infected ? "Yes" : "No"}</div>
                    <div>Took Infection: {ev.TookInfection ? "Yes" : "No"}</div>
                    <div>Was Infected: {ev.WasInfected ? "Yes" : "No"}</div>
                    <div>Probable Customers: {ev.ProbableCustomers}</div>
                    <div style={{ display: "flex", gap: "var(--space-3)" }}>
                        <span>Stable X: {ev.X}</span>
                        <span>Stable Y: {ev.Y}</span>
                        <span>Relevance: {ev.Relevance?.toFixed(2) ?? "—"}</span>
                    </div>
                    <div>Extra: {ev.Extra ?? ""}</div>
                    <div>When: {isNaN(when.getTime()) ? String(ev.When) : `${when.toDateString()} ${when.toTimeString()}`}</div>
                </div>
            </fieldset>
            <fieldset style={{ flex: "1 1 240px", minWidth: 0, border: "1px solid var(--color-border)", borderRadius: "var(--radius-1)", padding: "var(--space-3)" }}>
                <legend><strong>Customer Cart</strong></legend>
                <textarea
                    readOnly
                    value={ev.CurrentCart ?? ""}
                    style={{
                        width: "100%", height: "100%", minHeight: 400, resize: "vertical",
                        border: "none", background: "transparent", color: "inherit", fontSize: "var(--text-sm)",
                        fontFamily: "monospace",
                    }}
                />
            </fieldset>
        </div>
    );
}

function TrackingDetail({ ev }: { ev: EventProduct }) {
    // ProductEvent.When survives the TrackingEvents blob as a zone-qualified
    // instant: Newtonsoft parses it Kind=Utc and System.Text.Json writes the Z
    // back out. So it needs instant handling, not the verbatim treatment — that
    // stripped the Z and printed the UTC digits.
    const formatted = formatInstant(parseUtcTimestamp(ev.When)) || String(ev.When);

    return (
        <div style={{ flex: 1 }}>
            <fieldset style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-1)", padding: "var(--space-3)", maxWidth: 350 }}>
                <legend><strong>Tracking Data</strong></legend>
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)", fontSize: "var(--text-sm)" }}>
                    <div>When: {formatted}</div>
                    <div>X: {ev.X}</div>
                    <div>Y: {ev.Y}</div>
                    <div>Extra: {ev.Extra ?? ""}</div>
                </div>
            </fieldset>
        </div>
    );
}

/* ── Transaction grids ────────────────────────────────── */
function TransactionGrids({ transactionID }: { transactionID: number }) {
    return (
        <>
            <div style={{ marginBottom: "var(--space-4)" }}>
                <h3 className={styles.sectionTitle}>Transaction Head</h3>
                <DataGrid
                    key={`txHead-${transactionID}`}
                    name="transactionHeadSearch"
                    staticFilters={[{ sColumn: "iTransactionID", oValue: transactionID, Type: ParamType.Equal }]}
                    autoSearch
                    compact
                />
            </div>
            <div style={{ marginBottom: "var(--space-4)" }}>
                <h3 className={styles.sectionTitle}>Transaction Rows</h3>
                <DataGrid
                    key={`txRows-${transactionID}`}
                    name="transactionRowSearch"
                    staticFilters={[{ sColumn: "iTransactionID", oValue: transactionID, Type: ParamType.Equal }]}
                    autoSearch
                    compact
                />
            </div>
            <div style={{ marginBottom: "var(--space-4)" }}>
                <h3 className={styles.sectionTitle}>Payment Logs</h3>
                <DataGrid
                    key={`payments-${transactionID}`}
                    name="transactionPaymentSearch"
                    staticFilters={[{ sColumn: "iTransactionID", oValue: transactionID, Type: ParamType.Specified }]}
                    autoSearch
                    compact
                />
            </div>
        </>
    );
}
